import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { isAuthoritativeSvrOnSite } from "@shared/serviceOperations";
import {
  presentPastSiteVisits,
  presentUpcomingSiteVisits,
  siteVisitSearchWhen,
  sortTodaySiteVisits,
  type OperationalSiteVisitRow,
  type SiteVisitDateGroup,
} from "@shared/siteVisitListBuckets";
import {
  AlertTriangle,
  CalendarDays,
  Clock,
  DollarSign,
  MapPin,
  MessageCircle,
  Plus,
  Search,
  XCircle,
} from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  pending_review: "Pending Review",
  awaiting_payment: "Awaiting Payment",
  closed: "Closed",
  report_sent: "Closed (Report Sent)",
  waived: "Waived",
  cancelled: "Cancelled",
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800",
  pending_review: "bg-amber-100 text-amber-800",
  awaiting_payment: "bg-amber-100 text-amber-800",
  closed: "bg-gray-100 text-gray-700",
  report_sent: "bg-gray-100 text-gray-700",
  waived: "bg-purple-100 text-purple-800",
  cancelled: "bg-gray-100 text-gray-500",
};

export type SiteVisitListFilter = "today" | "upcoming" | "past" | "reports" | "needs_matching" | "all";

const TABS: { key: SiteVisitListFilter; label: string; countKey?: "today" | "upcoming" | "reports" | "needsMatching" }[] = [
  { key: "today", label: "Today", countKey: "today" },
  { key: "upcoming", label: "Upcoming", countKey: "upcoming" },
  { key: "past", label: "Past" },
  { key: "reports", label: "Reports", countKey: "reports" },
  { key: "needs_matching", label: "Needs Matching", countKey: "needsMatching" },
  { key: "all", label: "All Visits" },
];

export function SiteVisitOperationalList({
  filter,
  onFilter,
  search,
  onSearch,
  counts,
  items,
  mode,
  loading,
  error,
  onRetry,
  onOpen,
  onCreate,
  hasMore,
  onLoadMore,
  now,
  scheduledVisitsCount,
}: {
  filter: SiteVisitListFilter;
  onFilter: (filter: SiteVisitListFilter) => void;
  search: string;
  onSearch: (search: string) => void;
  counts: { today: number; upcoming: number; reports: number; needsMatching: number };
  items: OperationalSiteVisitRow[];
  mode: SiteVisitListFilter | "search";
  loading: boolean;
  error?: string | null;
  onRetry: () => void;
  onOpen: (id: number) => void;
  onCreate: () => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  now: Date;
  scheduledVisitsCount: number;
}) {
  const subtitle = mode === "search"
    ? "Searching all service visits"
    : filter === "today"
      ? "Visits scheduled for today"
      : filter === "upcoming"
        ? "Visits from tomorrow onward"
        : filter === "past"
          ? "Earlier visits, including ones still open"
          : filter === "all"
            ? "All recent service visits"
            : filter === "needs_matching"
              ? "Urgent visits waiting to be matched"
              : "Submitted site visit reports";

  return (
    <div data-testid="svr-list-page" className="max-w-4xl mx-auto py-8 px-4 space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">Service Visits</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Button
          onClick={onCreate}
          className="bg-black text-white hover:bg-gray-800 flex items-center gap-2 shrink-0"
        >
          <Plus className="h-4 w-4" />
          New Visit
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search by customer, visit number, or technician..."
            className="pl-9"
            aria-label="Search service visits"
          />
        </div>
        <div data-testid="svr-list-tabs" className="flex gap-2 flex-wrap">
          {TABS.map(({ key, label, countKey }) => {
            const count = countKey ? counts[countKey] : 0;
            const emphasized = key === "needs_matching" || key === "reports";
            return (
              <button
                key={key}
                type="button"
                data-testid={`svr-tab-${key}`}
                onClick={() => onFilter(key)}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  filter === key && mode !== "search"
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                }`}
              >
                {label}
                {count > 0 ? (
                  <span className={`ml-1 text-[10px] rounded-full px-1.5 py-0.5 ${
                    emphasized
                      ? key === "needs_matching" ? "bg-red-500 text-white" : "bg-amber-500 text-white"
                      : filter === key && mode !== "search" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                  }`}>
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-md animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-10 w-10 text-red-500 mb-3" />
          <p className="font-medium text-red-700">Failed to load site visits</p>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>Retry</Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          mode={mode}
          filter={filter}
          search={search}
          scheduledVisitsCount={scheduledVisitsCount}
          onFilter={onFilter}
        />
      ) : mode === "upcoming" || mode === "past" ? (
        <div data-testid={mode === "upcoming" ? "svr-upcoming-list" : "svr-past-list"} className="space-y-5">
          {(mode === "upcoming" ? presentUpcomingSiteVisits(items, now) : presentPastSiteVisits(items, now)).map((group) => (
            <DateGroup key={group.dateKey} group={group} onOpen={onOpen} />
          ))}
          {mode === "past" && hasMore ? (
            <Button type="button" variant="outline" data-testid="svr-past-more" onClick={onLoadMore}>Show older visits</Button>
          ) : null}
        </div>
      ) : (
        <div data-testid={mode === "today" ? "svr-today-list" : mode === "search" ? "svr-search-results" : "svr-legacy-list"} className="space-y-2">
          {(mode === "today" ? sortTodaySiteVisits(items) : items).map((visit) => (
            <VisitCard key={visit.id} visit={visit} onOpen={onOpen} now={now} showWhen={mode === "search"} />
          ))}
          {mode === "search" && hasMore ? (
            <p className="text-xs text-muted-foreground">Showing the first matches. Refine the search to narrow them.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function DateGroup({
  group,
  onOpen,
}: {
  group: SiteVisitDateGroup<OperationalSiteVisitRow>;
  onOpen: (id: number) => void;
}) {
  return (
    <section data-testid="svr-date-group">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{group.label}</h2>
      <div className="space-y-2">
        {group.visits.map((visit) => (
          <VisitCard key={visit.id} visit={visit} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

function VisitCard({
  visit,
  onOpen,
  now,
  showWhen,
}: {
  visit: OperationalSiteVisitRow;
  onOpen: (id: number) => void;
  now?: Date;
  showWhen?: boolean;
}) {
  const active = isAuthoritativeSvrOnSite({
    status: visit.status,
    arrivedAt: visit.arrivedAt,
    departedAt: visit.departedAt,
    cleoOnSite: visit.cleoOnSite,
  });
  const when = showWhen && now ? siteVisitSearchWhen(visit.visitDate, now) : null;
  return (
    <Card
      data-testid="svr-visit-row"
      className={cn("cursor-pointer hover:shadow-sm transition-shadow", active && "border-amber-300 bg-amber-50/70")}
      onClick={() => onOpen(visit.id)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold">{visit.visitNumber}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_COLORS[visit.status] ?? "bg-gray-100 text-gray-600"}`}>
                {STATUS_LABELS[visit.status] ?? visit.status}
              </span>
              {active ? (
                <span data-testid="svr-visit-active" className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 motion-safe:animate-pulse" aria-hidden />
                  Visit in progress
                </span>
              ) : null}
              {visit.visitType === "urgent" ? (
                <span className="flex items-center gap-0.5 text-xs text-red-700 font-medium">🔴 Urgent</span>
              ) : null}
              {visit.followUpRequired ? (
                <span className="flex items-center gap-0.5 text-xs text-amber-800 font-medium" title={visit.followUpNotes ?? "This visit needs manual review/matching"}>
                  <AlertTriangle className="h-3 w-3" />
                  Needs Manual Matching
                </span>
              ) : null}
              {visit.isCustomerUnmatched ? (
                <span className="flex items-center gap-0.5 text-xs text-red-700 font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  Unmatched
                </span>
              ) : null}
              {visit.billable ? (
                <span className="flex items-center gap-0.5 text-xs text-amber-700">
                  <DollarSign className="h-3 w-3" />
                  Billable
                </span>
              ) : null}
              {visit.waiverRequested && !visit.waiverApproved ? (
                <span className="flex items-center gap-0.5 text-xs text-purple-700">
                  <AlertTriangle className="h-3 w-3" />
                  Waiver Requested
                </span>
              ) : null}
              {visit.custWhatsappStatus === "sent" ? (
                <span className="flex items-center gap-0.5 text-xs text-green-700" title="WhatsApp confirmation sent to customer">
                  <MessageCircle className="h-3 w-3" />
                  WA Sent
                </span>
              ) : null}
              {visit.custWhatsappStatus === "failed" ? (
                <span className="flex items-center gap-0.5 text-xs text-red-600" title="WhatsApp confirmation failed">
                  <XCircle className="h-3 w-3" />
                  WA Failed
                </span>
              ) : null}
              {visit.custWhatsappStatus === "no_phone" ? (
                <span className="flex items-center gap-0.5 text-xs text-gray-400" title="No phone number on file">
                  <MessageCircle className="h-3 w-3" />
                  No Phone
                </span>
              ) : null}
            </div>
            <p className="font-medium text-sm mt-1">{visit.customerName}</p>
            {visit.isCustomerUnmatched && visit.customerAddress ? (
              <p className="text-xs text-muted-foreground mt-0.5">Raw input: {visit.customerAddress}</p>
            ) : null}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {when ? when.label : formatDate(visit.visitDate)}
              </span>
              {visit.arrivalWindow ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {visit.arrivalWindow}
                </span>
              ) : null}
              {visit.technicianName ? <span>{visit.technicianName}</span> : null}
              {visit.picName ? <span>PIC: {visit.picName}</span> : null}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({
  mode,
  filter,
  search,
  scheduledVisitsCount,
  onFilter,
}: {
  mode: SiteVisitListFilter | "search";
  filter: SiteVisitListFilter;
  search: string;
  scheduledVisitsCount: number;
  onFilter: (filter: SiteVisitListFilter) => void;
}) {
  const copy = search
    ? "Try adjusting your search"
    : mode === "today"
      ? "No service visits scheduled today."
      : mode === "upcoming"
        ? "No upcoming service visits."
        : mode === "past"
          ? "No earlier service visits."
          : "No site visits for this view yet";
  return (
    <div data-testid="svr-empty" className="flex flex-col items-center justify-center py-16 text-center">
      <MapPin className="h-10 w-10 text-muted-foreground/30 mb-3" />
      <p className="text-muted-foreground font-medium">No site visits found</p>
      {filter === "reports" && !search && scheduledVisitsCount > 0 ? (
        <>
          <p className="text-sm text-muted-foreground/70 mt-1">
            No reports yet. You have {scheduledVisitsCount} scheduled visit{scheduledVisitsCount === 1 ? "" : "s"}.
          </p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => onFilter("today")}>
            View Today's Visits
          </Button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground/70 mt-1">{copy}</p>
      )}
    </div>
  );
}
